import { useEffect, useState } from "react";
import { equalTo, onValue, orderByChild, query } from "firebase/database";
import { jobRequestsDbRef } from "../utils/dbRefs";
import { useAuth } from "../contexts/AuthContext";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


export const useShopAnalytics = () => {
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();
    const [completedJobs, setCompletedJobs] = useState(0);
    const [ongoingJobs, setOngoingJobs] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [customerActivityData, setCustomerActivityData] = useState([]);
    const [filesOverviewData, setFilesOverviewData] = useState([]);

    useEffect(() => {
        if (!currentUser?.uid) return;

        setLoading(true);

        const shopJobsQuery = query(jobRequestsDbRef, orderByChild("shop/uid"), equalTo(currentUser.uid));
        const unsubscribe = onValue(shopJobsQuery, (snapshot) => {
            const data = snapshot.val();

            if (!data) {
                setCompletedJobs(0);
                setOngoingJobs(0);
                setTotalFiles(0);
                setTotalCustomers(0);
                setCustomerActivityData([]);
                setLoading(false);
                return;
            }

            const values = Object.values(data);

            const myJobs = values;

            // ✅ Completed Jobs
            const completed = myJobs.filter(
                (job) => job.status === "Completed"
            ).length;

            // 🔄 Ongoing Jobs (received)
            const ongoing = myJobs.filter(
                (job) => job.status === "received"
            ).length;

            // 📁 Total Files (sum of all jobs)
            const filesCount = myJobs.reduce((sum, job) => {
                return sum + (job.files?.length || 0);
            }, 0);

            // 👥 Unique Customers (emails)
            const uniqueCustomers = new Set(
                myJobs.map((job) => job.customer?.email).filter(Boolean)
            );

            setCompletedJobs(completed);
            setOngoingJobs(ongoing);
            setTotalFiles(filesCount);
            setTotalCustomers(uniqueCustomers.size);


            // -----------------------------
            // CUSTOMER ACTIVITY (NEW)
            // -----------------------------
            const monthlyMap = Array.from({ length: 12 }, (_, i) => ({
                name: monthNames[i],
                loyal: new Set(),
                new: new Set(),
                unique: new Set(),
            }));

            myJobs.forEach((job) => {
                const date = job.createdAt ? new Date(job.createdAt) : null;
                if (!date) return;

                const monthIndex = date.getMonth();
                const email = job.customer?.email;
                if (!email) return;

                const month = monthlyMap[monthIndex];

                // unique always
                month.unique.add(email);

                // simple rule:
                // first time customer = "new"
                // repeat = "loyal"
                const firstIndex = myJobs.findIndex(
                    j => j.customer?.email === email
                );

                if (firstIndex === -1) return;

                const isFirstAppearance = firstIndex === myJobs.indexOf(job);

                if (isFirstAppearance) {
                    month.new.add(email);
                } else {
                    month.loyal.add(email);
                }
            });

            const chartData = monthlyMap.map(m => ({
                name: m.name,
                loyal: m.loyal.size,
                new: m.new.size,
                unique: m.unique.size,
            }));

            setCustomerActivityData(chartData);

            // -----------------------------
            // FILES OVERVIEW (NEW)
            // -----------------------------
            const monthlyFiles = Array.from({ length: 12 }, (_, i) => ({
                month: monthNames[i],
                Files: 0,
            }));

            myJobs.forEach((job) => {
                if (!job.createdAt) return;

                const date = new Date(job.createdAt);
                const monthIndex = date.getMonth();

                monthlyFiles[monthIndex].Files += (job.files?.length || 0);
            });

            // optional: fill empty months nicely (already 0 by default)
            setFilesOverviewData(monthlyFiles);

            setLoading(false);
        });


        return () => unsubscribe();
    }, [currentUser?.uid]);

    return {
        loading,
        completedJobs,
        ongoingJobs,
        totalFiles,
        totalCustomers,
        customerActivityData,
        filesOverviewData
    };
};
